import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'motion/react';
import { normalizeVideoUrl } from '../utils/media';
import CTAButton from './CTAButton';
import EditableText from './live-editor/EditableText';
import EditableRichText from './live-editor/EditableRichText';
import EditableMedia from './live-editor/EditableMedia';
import { useLiveEditorScope } from '../context/LiveEditorScopeContext';

interface HeroProps {
  title: React.ReactNode;
  subtitle?: string;
  cta?: {
    text: string;
    link: string;
    type?: 'button' | 'link';
  };
  imageUrl?: string;
  videoUrl?: string;
  alignment?: 'center' | 'bottom' | 'left';
  height?: string;
  className?: string;
  isLoading?: boolean;
  onEditTitle?: (newTitle: string) => void;
  onEditSubtitle?: (newSubtitle: string) => void;
  onEditCtaText?: (newCtaText: string) => void;
  onEditCtaLink?: (newCtaLink: string) => void;
  onEditImageUrl?: (newImageUrl: string) => void;
  onEditVideoUrl?: (newVideoUrl: string) => void;
}

export default function Hero({
  title,
  subtitle,
  cta,
  imageUrl,
  videoUrl,
  alignment = 'center',
  height = 'min-h-screen',
  className = '',
  isLoading = false,
  onEditTitle,
  onEditSubtitle,
  onEditCtaText,
  onEditCtaLink,
  onEditImageUrl,
  onEditVideoUrl
}: HeroProps) {
  const { isAdmin, isEditMode, isPreviewMode, canEdit } = useLiveEditorScope();
  const shouldReduceMotion = useReducedMotion();
  const [videoFailed, setVideoFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);

  const prevUrlsRef = useRef({ imageUrl, videoUrl });
  const normalizedUrl = useMemo(() => normalizeVideoUrl(videoUrl || ''), [videoUrl]);

  // URL Change Detection: only reset loaded states if the image/video URLs actually change.
  // This prevents layout flashing and skeleton updates when identical cached data resolves from the API.
  useEffect(() => {
    if (imageUrl !== prevUrlsRef.current.imageUrl || videoUrl !== prevUrlsRef.current.videoUrl) {
      setIsMediaLoaded(false);
      setIsVideoReady(false);
      setVideoFailed(false);
      setImageFailed(false);
      prevUrlsRef.current = { imageUrl, videoUrl };
    }
  }, [imageUrl, videoUrl]);

  const videoMimeType = useMemo(() => {
    if (!normalizedUrl) return undefined;
    const clean = normalizedUrl.split('?')[0].toLowerCase();
    if (clean.endsWith('.mp4')) return 'video/mp4';
    if (clean.endsWith('.webm')) return 'video/webm';
    if (clean.endsWith('.ogv') || clean.endsWith('.ogg')) return 'video/ogg';
    return undefined;
  }, [normalizedUrl]);

  useEffect(() => {
    if (!imageUrl && !videoUrl) {
      setIsMediaLoaded(true);
      return;
    }

    // Safety timeout: if media doesn't load within 2 seconds, force it
    // This ensures content visibility even if the image is slow or fails
    const timer = setTimeout(() => {
      setIsMediaLoaded(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [videoUrl, imageUrl]);

  if (isLoading) {
    return (
      <div className={`relative w-full overflow-hidden flex flex-col ${height} ${className}`}>
        {/* Background Media Placeholder / Shimmer */}
        <div className="absolute inset-0 z-0 bg-paper">
          <div className="absolute inset-0 bg-[#261F1F]/40 mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#261F1F]/60 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-white/5 animate-pulse" />
          <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none bg-[url('/textures/pinstripe-dark.png')]" />
        </div>

        {/* Content Container Skeleton */}
        <div className={`relative z-10 flex flex-col flex-1 w-full max-w-[1400px] mx-auto px-6 md:px-12 ${alignment === 'center' ? 'items-center justify-center text-center' : 'justify-end pb-12 md:pb-20'
          }`}>
          <div className={`w-full ${alignment === 'center' ? 'max-w-4xl flex flex-col items-center' : 'max-w-3xl'}`}>
            {/* Subtitle Skeleton */}
            <div className="h-4 bg-white/20 w-32 mb-4 animate-pulse" />

            {/* Title Skeletons */}
            <div className={`space-y-3 w-full mb-10 ${alignment === 'center' ? 'flex flex-col items-center' : ''}`}>
              <div className="h-10 md:h-16 lg:h-20 bg-white/20 w-3/4 animate-pulse" />
              <div className="h-10 md:h-16 lg:h-20 bg-white/20 w-1/2 animate-pulse" />
            </div>

            {/* CTA Skeleton */}
            <div className="h-12 bg-white/20 w-40 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  const hasVideo = !!normalizedUrl && !videoFailed && !shouldReduceMotion;
  const showSkeleton = !isMediaLoaded && !hasVideo;

  return (
    <div className={`relative w-full overflow-hidden flex flex-col bg-ink ${height} ${className}`}>
      {/* Background Media wrapped with EditableMedia */}
      <EditableMedia
        currentImageUrl={imageUrl}
        currentVideoUrl={videoUrl}
        onImageChange={onEditImageUrl}
        onVideoChange={onEditVideoUrl}
        label="Hero Banner"
        className="absolute inset-0 w-full h-full z-0 overflow-hidden"
      >
        <div className="absolute inset-0 w-full h-full z-0">
          <AnimatePresence>
            {showSkeleton && (
              <motion.div
                key="hero-skeleton"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 z-10 bg-paper"
              >
                <motion.div
                  animate={{
                    x: ['-100%', '100%'],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent w-full h-full"
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Static Image Layer (always rendered if imageUrl is provided) */}
          {imageUrl && !imageFailed && (
            <img
              src={imageUrl}
              alt="Hero Background"
              onLoad={() => setIsMediaLoaded(true)}
              onError={() => setImageFailed(true)}
              className="absolute inset-0 w-full h-full object-cover object-[10%_0%]"
              referrerPolicy="no-referrer"
              fetchPriority="high"
              loading="eager"
            />
          )}

          {/* Video Layer (renders on top of image if hasVideo is true) */}
          {hasVideo && (
            <video
              autoPlay
              loop
              muted
              playsInline
              poster={imageUrl}
              onCanPlay={() => {
                setIsVideoReady(true);
                setIsMediaLoaded(true);
              }}
              onLoadedData={() => {
                setIsVideoReady(true);
                setIsMediaLoaded(true);
              }}
              onPlay={() => {
                setIsVideoReady(true);
                setIsMediaLoaded(true);
              }}
              onError={() => setVideoFailed(true)}
              className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${
                isVideoReady || !imageUrl ? 'opacity-100' : 'opacity-0'
              }`}
              preload="auto"
              key={normalizedUrl}
            >
              <source src={normalizedUrl} {...(videoMimeType ? { type: videoMimeType } : {})} />
            </video>
          )}

          {/* Editorial Overlays */}
          <div className="absolute inset-0 bg-[#261F1F]/40 mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#261F1F]/60 via-transparent to-transparent" />
          <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none bg-[url('/textures/pinstripe-dark.png')]" />
        </div>
      </EditableMedia>

      {/* Content Container */}
      <div className={`relative z-10 flex flex-col flex-1 w-full max-w-[1400px] mx-auto px-6 md:px-12 ${alignment === 'center' ? 'items-center justify-center text-center' : 'justify-end pb-12 md:pb-20'
        }`}>
        <div className={alignment === 'center' ? 'max-w-4xl' : 'max-w-3xl'}>
          {canEdit && onEditSubtitle && typeof subtitle === 'string' ? (
            <div className={`mb-4 ${alignment === 'center' ? 'text-center' : 'text-left'}`}>
              <EditableRichText
                value={subtitle}
                onChange={onEditSubtitle}
                as="p"
                label="Hero Subtitle"
                className={`text-label text-white/70 uppercase tracking-widest ${alignment === 'center' ? 'text-center' : 'text-left'}`}
              />
            </div>
          ) : subtitle ? (
            <motion.p
              initial={shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={shouldReduceMotion ? { duration: 0 } : { duration: 1, delay: 0.4, ease: [0.25, 1, 0.5, 1] }}
              className={`text-label text-white/70 mb-4 uppercase tracking-widest ${alignment === 'center' ? 'text-center' : 'text-left'}`}
              dangerouslySetInnerHTML={{ __html: subtitle }}
            />
          ) : null}

          {canEdit && onEditTitle && typeof title === 'string' ? (
            <div className="mb-10">
              <EditableRichText
                value={title}
                onChange={onEditTitle}
                as="h1"
                multiline
                label="Hero Headline"
                className={`${alignment === 'center' ? 'text-5xl md:text-7xl lg:text-8xl text-center' : 'text-display text-left'} font-serif leading-tight text-white`}
              />
            </div>
          ) : typeof title === 'string' ? (
            <motion.h1
              initial={shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={shouldReduceMotion ? { duration: 0 } : { duration: 1.2, delay: 0.55, ease: [0.25, 1, 0.5, 1] }}
              className={`${alignment === 'center' ? 'text-5xl md:text-7xl lg:text-8xl text-center' : 'text-display text-left'} font-serif mb-10 leading-tight text-white`}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          ) : (
            <motion.h1
              initial={shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={shouldReduceMotion ? { duration: 0 } : { duration: 1.2, delay: 0.55, ease: [0.25, 1, 0.5, 1] }}
              className={`${alignment === 'center' ? 'text-5xl md:text-7xl lg:text-8xl text-center' : 'text-display text-left'} font-serif mb-10 leading-tight text-white`}
            >
              {title}
            </motion.h1>
          )}

          {cta && (
            <motion.div
              initial={shouldReduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={shouldReduceMotion ? { duration: 0 } : { duration: 1, delay: 0.75, ease: [0.25, 1, 0.5, 1] }}
              className={`flex flex-wrap items-center gap-6 ${alignment === 'center' ? 'justify-center' : 'justify-start'}`}
            >
              {canEdit && onEditCtaText ? (
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <EditableText
                    value={cta.text}
                    onChange={onEditCtaText}
                    as="span"
                    label="Teks Tombol CTA"
                    className="text-xs font-semibold px-4 py-3 bg-white/10 hover:bg-white/20 text-white border border-white/30 rounded"
                  />
                  {onEditCtaLink && (
                    <EditableText
                      value={cta.link}
                      onChange={onEditCtaLink}
                      as="span"
                      label="Link URL Tombol"
                      className="text-[11px] font-mono text-white/70 px-2 py-1 bg-black/40 rounded border border-white/10"
                    />
                  )}
                </div>
              ) : (
                <CTAButton
                  to={cta.link}
                  variant="transparent"
                  className="w-full sm:w-auto"
                >
                  {cta.text}
                </CTAButton>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
